import React from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import {
  Alert, Avatar, Button, ConfirmDialog, TextInput, phone as phoneLib,
} from '@parsina/framework';
import MoneyInput from '../../components/common/MoneyInput';
import { C, CT } from '@parsina/framework/tokens';
import { useTranslation, LTR_TEXT, inDocumentCurrency } from '../../i18n';
import { explainLegs, lineInsuranceState, SOURCE } from '../../lib/insurance';
import { useInsurerName } from '../../hooks/useInsurance';
import { useShortcut } from '../../lib/shortcuts.jsx';
import { usePatientInHouseOrders } from '../../hooks/useMedicalService';
import {
  PAYMENT_METHODS, STATUS_META, ITEM_TYPE_META, DEFAULT_PAYMENT_METHOD,
  settleState, withLineItems, canRemoveLine, resolveDiscount, discountLabel,
  validatePayment, canSubmitPayment, journeySteps, issuanceBadge,
  lineEditPreview, validateLineEdit, validateInsuranceOverride, validateInvoiceDiscount,
} from '../../lib/billing';
import MobileSheet from '../MobileSheet';
import useErrorReveal from '../useErrorReveal';
import MobileFormSheet, { MobileField } from '../MobileFormSheet';
import MobileMoneyRow from '../MobileMoneyRow';
import MobileRowMenu from '../MobileRowMenu';
import ChangeServiceDialog from '../../components/billing/ChangeServiceDialog';
import { MobileMoneyRowSkeleton } from './MobileSheetSkeletons';
import { moneyErrorText } from '../../lib/currencyErrors';

/**
 * Taking a payment, on a phone. The reason the cashier screen has a mobile
 * surface at all.
 *
 * Issuing a complex invoice stays a desk job; TAKING money is the most common
 * standing-up money action in a clinic, and refusing it on a phone is why staff
 * photograph invoices. So this sheet carries the whole settle flow — the lines,
 * the waterfall, issuance, the amount, the method, the reference and the
 * patient's on-account credit — and it does so without re-deriving a single
 * figure: every rule below comes from `lib/billing.js`, which the desktop
 * drawer reads too.
 *
 * The rules that are easy to lose in a rewrite, and are not:
 *
 *   • `shownStatus` is derived from MONEY, not from the wire status — the two
 *     legitimately disagree for a beat after a payment clears.
 *   • `frozen` (an ISSUED invoice) means the lines are never edited or removed; a
 *     correction is a negative line or void-and-reissue.
 *   • `canRemove` needs no payment, no settlement and no issuance, and the VISIT
 *     line is never removable at all.
 *   • Adding a line to a SETTLED invoice reopens it, so it asks first, with the
 *     full consequence including "the new line will not be removable".
 *   • The insurer's share is editable only where nothing computed it; overriding a
 *     computed one needs `payments.manage` and a mandatory reason.
 *   • An empty invoice's typed amount becomes the VISIT line BEFORE the payment.
 *   • `recordPayment` goes WITHOUT a `shiftId`, so the service banks it into the
 *     drawer that is actually open.
 *   • Overpayment is NAMED, never rounded into a tidy zero.
 *
 * The summary is stacked full-width rows rather than a KPI grid: a quarter of
 * 320px cannot hold a large toman figure, and the row would take the page into
 * horizontal scroll.
 */
function MobileSettleSheet({
  open,
  invoice,
  onClose,
  onPaymentDone,
  tariffs = [],
  recordPayment,
  addLineItem,
  updateLineItem,
  overrideLineInsurance,
  removeLineItem,
  setInvoiceDiscount,
  getPayments,
  issueInvoice,
  applyCredit,
  getCreditBalance,
  canOverrideInsurance = false,
  openAddItem = false,
  openSection = null,
  // «خدمت نوبت» — the same settlement the desktop drawer reads, so the phone
  // and the desk cannot tell different stories about one appointment
  // (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §10).
  settlement = null,
  onChangeService,
  onReconcileService,
}) {
  const { t, moneyUnit, formatNumber, formatMoney, formatTime, currency, toDisplayAmount, parseMoney } = useTranslation();

  const [method, setMethod] = React.useState(DEFAULT_PAYMENT_METHOD);
  const [reference, setReference] = React.useState('');
  const [creditBalance, setCreditBalance] = React.useState(0);
  const [issuing, setIssuing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [addingItem, setAddingItem] = React.useState(false);
  const [reopenAsk, setReopenAsk] = React.useState(false);
  const [discountOpen, setDiscountOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  // «تغییر خدمت» / «هم‌سان‌سازی» — the same two acts the desk has, in a sheet.
  const [serviceAsk, setServiceAsk] = React.useState(false);
  const [reconciling, setReconciling] = React.useState(false);
  // «قیمت دستی» — the same mandatory reason the desk gives (§D7).
  const [manualReason, setManualReason] = React.useState('');
  const [error, setErrorText] = React.useState('');
  /**
   * Bumped on every refusal, including a REPEAT of the same one.
   *
   * The scroll below keys on this rather than on `error`, and that is the whole
   * point of having it: «صندوقی به نام شما باز نیست» twice in a row is the same
   * string, so an effect watching the text would fire once and never again. The
   * operator taps پرداخت, scrolls away, taps it again — and the second tap would
   * look like nothing happened, which is the bug this fixes.
   */
  const [errorSeq, setErrorSeq] = React.useState(0);
  const errorRef = React.useRef(null);

  /** Every refusal in this sheet goes through here, so none can forget to scroll. */
  const setError = React.useCallback((message) => {
    setErrorText(message);
    if (message) setErrorSeq((n) => n + 1);
  }, []);

  /**
   * Take the operator to the refusal — the shared behaviour every sheet in
   * this folder needs, because they all dock the commit button in the footer
   * and render the error at the top of the scroll body.
   */
  useErrorReveal(errorRef, error, errorSeq);
  const [localInv, setLocalInv] = React.useState(invoice);
  const [paid, setPaid] = React.useState(0);
  const [paidLoaded, setPaidLoaded] = React.useState(false);
  const [payAmount, setPayAmount] = React.useState('');
  const [amountTouched, setAmountTouched] = React.useState(false);

  // This patient's in-house orders, so the desk can bill them onto the visit
  // invoice in one tap instead of hand-picking a tariff.
  const { orders: patientOrders, refresh: refreshPatientOrders } = usePatientInHouseOrders(invoice?.patientId);

  const {
    lineDiscount, subtotal, invDiscount, payable, outstanding,
    isVoid, isSettled, frozen, canRemove, overpaid,
    shownStatus, isEmptyInvoice, maxChargeable,
  } = settleState(localInv, paid, paidLoaded);

  const statusMeta = STATUS_META(t)[shownStatus] || STATUS_META(t).DRAFT;
  const badge = issuanceBadge(t, localInv);
  const methods = PAYMENT_METHODS(t);
  const activeMethod = methods[method];

  const refreshPaid = React.useCallback(async () => {
    try {
      const ps = await getPayments(localInv.id);
      setPaid(ps.reduce((s, p) => s + (p.amount || 0), 0));
    } catch { /* keep the last known paid total */ } finally { setPaidLoaded(true); }
  }, [getPayments, localInv.id]);

  React.useEffect(() => { refreshPaid(); }, [refreshPaid]);

  // What the clinic holds on account for this patient («علی‌الحساب»), so the desk
  // can settle out of it before asking for cash. Cosmetic on failure: no balance,
  // no button.
  React.useEffect(() => {
    if (!getCreditBalance || !invoice?.patientId) return undefined;
    let cancelled = false;
    getCreditBalance(invoice.patientId)
      .then((b) => { if (!cancelled) setCreditBalance(b?.balanceRial || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [getCreditBalance, invoice?.patientId]);

  // Opened straight from reception's «افزودن قلم»: jump to the picker, or to the
  // reopen confirmation if the invoice is already settled. Waits for the paid
  // total, since `isSettled` is meaningless before it lands. Runs once.
  const autoOpened = React.useRef(false);
  React.useEffect(() => {
    if (autoOpened.current || !openAddItem || !paidLoaded || isVoid) return;
    autoOpened.current = true;
    if (isSettled) setReopenAsk(true);
    else setAddingItem(true);
  }, [openAddItem, paidLoaded, isSettled, isVoid]);

  // Default the amount to the full outstanding balance until the cashier edits it.
  // For an empty invoice there is no balance yet, so the field stays blank for the
  // appointment fee.
  //
  // `payAmount` is TOMAN — what sits in the box the cashier reads and types in.
  // `payRial` is what is actually charged. Keeping rials in the box was the whole
  // bug: a 500,000 ت visit asked the patient for 5,000,000 ت.
  React.useEffect(() => {
    if (!amountTouched && !isEmptyInvoice) setPayAmount(String(toDisplayAmount(outstanding) ?? 0));
  }, [outstanding, amountTouched, isEmptyInvoice]);

  const payRial = parseMoney(payAmount);

  // ── Writes ────────────────────────────────────────────────────────────────

  const handleIssue = async () => {
    if (!issueInvoice) return;
    setIssuing(true); setError('');
    try {
      const updated = await issueInvoice(localInv.id);
      setLocalInv((prev) => ({ ...prev, ...updated }));
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.issueFailed'));
    } finally {
      setIssuing(false);
    }
  };

  const handleApplyCredit = async () => {
    if (!applyCredit) return;
    const amount = Math.min(creditBalance, outstanding);
    if (!(amount > 0)) return;
    setSaving(true); setError('');
    try {
      await applyCredit(localInv.id, amount);
      onPaymentDone();
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.creditApplyFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveItem = async (lineItemId) => {
    try {
      await removeLineItem(localInv.id, lineItemId);
      setLocalInv((prev) => withLineItems(prev, prev.lineItems.filter((li) => li.id !== lineItemId)));
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.lineDeleteFailed'));
    }
  };

  // Rethrows: the editor owns the error, so a rejected edit is shown against the
  // fields the cashier is still looking at and the sheet stays open.
  const handleEditItem = async (lineItemId, patch) => {
    const item = await updateLineItem({ lineItemId, ...patch });
    setLocalInv((prev) => withLineItems(prev, prev.lineItems.map((li) => (li.id === item.id ? item : li))));
  };

  // A separate call from the edit because it is a separate act: its own
  // permission, a mandatory reason, and the service keeps what the rules said
  // beside what the person chose. Rethrows, for the same reason.
  const handleOverrideInsurance = async (lineItemId, insuranceShare, reason) => {
    const item = await overrideLineInsurance(lineItemId, insuranceShare, reason);
    setLocalInv((prev) => withLineItems(prev, prev.lineItems.map((li) => (li.id === item.id ? item : li))));
  };

  const handleSetDiscount = async (discountType, discountValue) => {
    const updated = await setInvoiceDiscount(localInv.id, discountType, discountValue);
    setLocalInv((prev) => ({ ...prev, ...updated }));
    setDiscountOpen(false);
  };

  // One-tap bill of an in-house order. The line carries sourceId = the ORDER id
  // and catalogId = the service (NOT a tariff id), so it traces back to the order
  // that caused it.
  const handleBillOrder = async (order) => {
    setError('');
    try {
      const item = await addLineItem({
        invoiceId: localInv.id,
        itemType: 'MEDICAL_SERVICE',
        sourceId: order.id,
        catalogId: order.catalogId,
        // The DECLARATION prices it, not the number this sheet last loaded
        // (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §D3).
        serviceDeclarationId: order.declarationId || undefined,
        priceFromDeclaration: !!order.declarationId,
        description: order.description,
        quantity: order.sessionCount || 1,
        unitTariff: order.unitPrice,
      });
      setLocalInv((prev) => withLineItems(prev, [...prev.lineItems, item]));
      await refreshPatientOrders();
      setAddingItem(false);
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.lineAddFailed'));
    }
  };

  // «هم‌سان‌سازی» — re-price the invoice from the service the appointment
  // names. One action for one state; the desk does not choose HOW (§D6).
  const handleReconcile = async () => {
    if (!onReconcileService) return;
    setReconciling(true); setError('');
    try {
      const next = await onReconcileService();
      if (next?.invoice) setLocalInv(next.invoice);
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.lineAddFailed'));
    } finally {
      setReconciling(false);
    }
  };

  const handlePickTariff = async (tariff) => {
    setAddingItem(false);
    try {
      const item = await addLineItem({
        invoiceId: localInv.id,
        itemType: 'MEDICAL_SERVICE',
        // No sourceId: the server mints `desk:<uuid>`, so a declaration id
        // never sits there posing as the order that caused the line (§F8).
        sourcePrefix: 'desk',
        catalogId: tariff.catalogId || undefined,
        serviceDeclarationId: tariff.id,
        priceFromDeclaration: true,
        description: tariff.description,
        quantity: 1,
        unitTariff: tariff.unitPrice,
        discount: 0,
        insuranceShare: 0,
      });
      setLocalInv((prev) => withLineItems(prev, [...prev.lineItems, item]));
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.lineAddFailed'));
    }
  };

  const handlePay = async () => {
    // Rials from here on: the toman in the box converts once, at the boundary.
    const amt = payRial;
    const refusal = validatePayment({ t, method, reference, amountRial: amt, maxChargeable });
    if (refusal) { setError(refusal); return; }
    if (isEmptyInvoice && !manualReason.trim()) {
      setError(t('cashier.manualPrice.reasonRequired'));
      return;
    }
    setSaving(true); setError('');
    try {
      // Empty invoice (a reception appointment with no priced fee yet): the typed
      // amount IS the appointment fee — add it as a line before charging.
      if (isEmptyInvoice) {
        const item = await addLineItem({
          invoiceId: localInv.id,
          itemType: 'VISIT',
          reason: manualReason.trim(),
          sourceId: localInv.appointmentId || localInv.id,
          // The appointment's own service when it has one, so even a typed fee
          // is recorded against WHAT the patient came for (§D7).
          serviceDeclarationId: settlement?.service?.declarationId || undefined,
          description: settlement?.service?.label || t('common.entity.visit'),
          quantity: 1,
          unitTariff: amt,
          discount: 0,
          insuranceShare: 0,
        });
        setLocalInv((prev) => ({ ...prev, lineItems: [...prev.lineItems, item] }));
      }
      // No shiftId: the service banks the money into the drawer that is actually
      // OPEN. Sending the INVOICE's shift put today's cash into the shift the
      // invoice was raised in — for an invoice raised by the encounter.ended
      // event, no shift at all — so the payment vanished from every drawer total
      // the clinic reconciles against.
      await recordPayment({
        invoiceId: localInv.id,
        patientId: localInv.patientId,
        amount: amt,
        paymentMethod: method,
        referenceNumber: reference.trim() || undefined,
      });
      onPaymentDone();
    } catch (e) {
      setError(moneyErrorText(e, t) || t('errors.cashier.paymentFailed'));
    } finally {
      setSaving(false);
    }
  };

  // A paired keyboard (a tablet under 768px) still commits from anywhere in the
  // sheet. Registered in the shortcut registry, never as a local window listener,
  // so precedence and the caret rule keep working. `saving` unbinds it, so a slow
  // network cannot be double-charged by a second press.
  useShortcut('ctrl+s', handlePay, {
    label: t('reception.actions.takePayment'),
    group: t('accounting.entity.invoice'),
    enabled: open && !saving && !isSettled,
  });

  const canPay = !saving && canSubmitPayment({ t, method, amountRial: payRial, maxChargeable });
  const patientName = localInv.patientName
    || t('patients.placeholder.byId', { id: localInv.patientId?.slice(-6) || '' });
  const editingItem = localInv.lineItems.find((li) => li.id === editingId) || null;

  return (
    <>
      <MobileSheet
        open={open}
        onClose={onClose}
        title={t('cashier.mobile.settleTitle')}
        fullHeight
        snap={1}
        closeLabel={t('common.actions.close')}
        data-testid="cashier-settle-sheet"
        footer={isSettled ? (
          <Button
            variant="outline"
            onClick={onClose}
            style={{ width: '100%', minHeight: '2.75rem' }}
            data-testid="cashier-settle-close"
          >
            {t('common.actions.close')}
          </Button>
        ) : (
          <Button
            onClick={handlePay}
            disabled={!canPay}
            style={{ width: '100%', minHeight: '2.75rem' }}
            data-testid="cashier-settle-pay"
          >
            {saving
              ? t('common.state.saving')
              : t('cashier.actions.payWith', {
                method: activeMethod?.short,
                amount: formatMoney(payRial > 0 ? payRial : 0),
              })}
          </Button>
        )}
      >
        <div style={{ padding: '0.875rem 1rem', display: 'grid', gap: '1.125rem' }}>
          {/* A form reports inline. The operator is looking at this sheet; a toast
              behind it that expires in three seconds is a message they did not read. */}
          {error ? (
            <div ref={errorRef}>
              <Alert variant="error" role="alert" data-testid="cashier-settle-error">{error}</Alert>
            </div>
          ) : null}

          {/* ① Who, and what state their invoice is in. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }} data-testid="cashier-settle-identity">
            <Avatar name={patientName} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                dir="auto"
                style={{
                  fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {patientName}
              </div>
              {localInv.patientPhone ? (
                <div style={{ fontSize: '0.6875rem', color: C.textMuted }} {...LTR_TEXT}>
                  {phoneLib.forDisplay(localInv.patientPhone)}
                </div>
              ) : null}
            </div>
            <span
              data-testid="cashier-settle-status"
              style={{
                flexShrink: 0, fontSize: '0.625rem', fontWeight: 700,
                padding: '0.1875rem 0.625rem', borderRadius: 999,
                background: statusMeta.bg, color: statusMeta.text, whiteSpace: 'nowrap',
              }}
            >
              {statusMeta.label}
            </span>
          </div>

          {/* «خدمت نوبت» — two lines on a phone rather than the desk's single
              strip, because four facts side by side do not fit 320px without
              horizontal scroll (MOBILE_WEB_PWA §4). Same facts, same order. */}
          {settlement ? (
            <div
              data-testid="settle-chain"
              style={{
                padding: '0.5rem 0.75rem', borderRadius: '0.625rem',
                background: C.bg, border: `1px solid ${C.border}`,
                fontSize: '0.75rem', lineHeight: 1.7, color: C.textMuted,
              }}
            >
              <div>
                {t('cashier.chain.booked')}{' '}
                {settlement.appointment?.dateTime
                  ? formatTime(Number(settlement.appointment.dateTime)) : '—'}
                {settlement.appointment?.arrivedAt ? (
                  <>{' · '}{t('cashier.chain.arrived')}{' '}
                    {formatTime(Number(settlement.appointment.arrivedAt))}</>
                ) : null}
              </div>
              <div style={{ color: C.textPrimary, fontWeight: 700, marginTop: '0.125rem' }}>
                {settlement.service ? settlement.service.label : '—'}
                {settlement.service?.unitPriceRial ? (
                  <span style={{ color: C.textMuted, fontWeight: 400 }}>
                    {' · '}{formatMoney(settlement.service.unitPriceRial, { currency: settlement?.currency })}{' '}
                    {moneyUnit('short', settlement?.currency)}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* One state, one sentence, one action — computed by the BFF (§D6). */}
          {settlement && settlement.consistency !== 'OK'
            && settlement.consistency !== 'LEGACY'
            && settlement.consistency !== 'NO_INVOICE' ? (
              <div
                data-testid={`settle-consistency-${settlement.consistency}`}
                style={{
                  display: 'grid', gap: '0.5rem',
                  padding: '0.625rem 0.75rem', borderRadius: '0.625rem',
                  background: settlement.consistency === 'SERVICE_MISMATCH'
                    ? '#FEF2F2' : C.amberLight,
                  fontSize: '0.75rem', lineHeight: 1.7,
                  color: settlement.consistency === 'SERVICE_MISMATCH' ? '#991B1B' : CT.warnInk,
                }}
              >
                <span>{t(`cashier.consistency.${settlement.consistency}`)}</span>
                {settlement.consistency === 'SERVICE_MISMATCH' && onReconcileService ? (
                  <Button
                    variant="outline"
                    onClick={handleReconcile}
                    disabled={reconciling}
                    style={{ width: '100%', minHeight: '2.75rem' }}
                  >
                    {t('cashier.consistency.reconcile')}
                  </Button>
                ) : null}
              </div>
            ) : null}

          {/* «پیش‌فاکتور» / «فاکتور #{n}» is a money-correctness fact, so it renders
              whatever else is on screen — with its explanation as visible text,
              because a phone has no hover to reveal a `title`. */}
          {!isVoid && (
            <div
              data-testid="cashier-settle-issuance-badge"
              style={{
                padding: '0.5rem 0.75rem', borderRadius: '0.625rem',
                background: badge.frozen ? C.tealLight : C.amberLight,
              }}
            >
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: badge.frozen ? C.teal : C.amber }}>
                {badge.label}
              </div>
              <div style={{ fontSize: '0.6875rem', color: C.textSecondary, marginTop: '0.125rem' }}>
                {badge.hint}
              </div>
            </div>
          )}

          {/* ② نوبت‌دهی → ویزیت → صدور → تسویه */}
          <div
            role="list"
            data-testid="cashier-settle-journey"
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              border: `1px solid ${C.border}`, borderRadius: '0.75rem', overflow: 'hidden',
            }}
          >
            {journeySteps(t, { hasLines: localInv.lineItems.length > 0, frozen, isSettled }).map((step) => (
              <div
                key={step.id}
                role="listitem"
                data-state={step.done ? 'done' : 'pending'}
                style={{
                  padding: '0.5rem 0.25rem', textAlign: 'center',
                  background: step.done ? C.greenLight : 'transparent',
                }}
              >
                <div style={{ fontSize: '1rem' }} aria-hidden="true">{step.icon}</div>
                <div style={{
                  fontSize: '0.625rem', fontWeight: 600, marginTop: '0.125rem',
                  color: step.done ? C.green : C.textMuted,
                }}
                >
                  {step.label}
                </div>
              </div>
            ))}
          </div>

          {/* ③ اقلام فاکتور */}
          <section data-testid="cashier-settle-lines" style={{ display: 'grid', gap: '0.5rem' }}>
            <SectionHead title={t('cashier.invoice.lineItems')} />

            {localInv.lineItems.length === 0 ? (
              <p style={{
                margin: 0, padding: '1rem', textAlign: 'center',
                fontSize: '0.8125rem', color: C.textMuted,
                border: `1px dashed ${C.border}`, borderRadius: '0.625rem',
              }}
              >
                {t('cashier.invoice.noLines')}
              </p>
            ) : localInv.lineItems.map((item) => {
              // The PRIMARY line is changed by re-pointing the APPOINTMENT, never
              // by editing the invoice — the same rule the desk keeps (§D1).
              const isPrimary = item.role === 'PRIMARY';
              return (
                <LineCard
                  key={item.id}
                  item={item}
                  canEdit={!isVoid && !frozen && !isPrimary}
                  canRemove={canRemoveLine(item, canRemove) && !isPrimary}
                  onEdit={() => setEditingId(item.id)}
                  onRemove={() => handleRemoveItem(item.id)}
                  roleLabel={ROLE_LABELS.has(item.role) ? t(`cashier.line.role.${item.role}`) : ''}
                  onChangeService={isPrimary && onChangeService && !frozen && !isVoid
                    ? () => setServiceAsk(true) : null}
                />
              );
            })}

            {/* Available on a settled invoice too: a service given after
                settlement reopens the invoice as a new balance. */}
            {!isVoid && (
              <Button
                variant="outline"
                onClick={() => (isSettled ? setReopenAsk(true) : setAddingItem(true))}
                style={{ width: '100%', minHeight: '2.75rem', gap: '0.375rem' }}
                data-testid="cashier-settle-add-line"
              >
                <Plus size={16} aria-hidden="true" />
                {t('cashier.invoice.addLine')}
              </Button>
            )}
          </section>

          {/* ④ The waterfall, gross → still owed, as full-width rows. */}
          <section
            data-testid="cashier-settle-summary"
            aria-label={t('cashier.mobile.invoiceSummary')}
            style={{
              display: 'grid', gap: '0.25rem',
              background: C.bg, border: `1px solid ${C.border}`,
              borderRadius: '0.75rem', padding: '0.75rem 0.875rem',
            }}
          >
            <MobileMoneyRow
              label={t('cashier.invoice.linesTotal')}
              value={formatMoney(localInv.totalAmount, { currency: localInv?.currency })}
              unit={moneyUnit('short', localInv?.currency)}
              data-testid="cashier-settle-lines-total"
            />
            {lineDiscount > 0 && (
              <MobileMoneyRow
                label={t('cashier.invoice.lineDiscounts')}
                value={`−${formatMoney(lineDiscount)}`}
                unit={moneyUnit('short')}
                tone="good"
                data-testid="cashier-settle-line-discounts"
              />
            )}
            {localInv.insuranceShare > 0 && (
              <MobileMoneyRow
                label={t('accounting.fields.insuranceShare')}
                value={`−${formatMoney(localInv.insuranceShare, { currency: localInv?.currency })}`}
                unit={moneyUnit('short')}
                tone="warn"
                data-testid="cashier-settle-insurance"
              />
            )}

            <MobileMoneyRow
              label={t('cashier.discount.wholeInvoice')}
              value={invDiscount > 0
                ? `−${discountLabel({ ...localInv, discount: invDiscount }, t, formatNumber, formatMoney)}`
                : '—'}
              tone={invDiscount > 0 ? 'good' : 'muted'}
              data-testid="cashier-settle-invoice-discount"
            />
            {!isVoid && !frozen && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDiscountOpen(true)}
                style={{ minHeight: '2.75rem', justifySelf: 'stretch' }}
                data-testid="cashier-settle-edit-discount"
              >
                {invDiscount > 0 ? t('common.actions.edit') : t('cashier.invoice.addDiscount')}
              </Button>
            )}

            <MobileMoneyRow
              label={t('cashier.invoice.patientShare')}
              value={formatMoney(payable)}
              unit={moneyUnit('short')}
              data-testid="cashier-settle-payable"
            />
            {paid > 0 && (
              <MobileMoneyRow
                label={t('cashier.invoice.paidSoFar')}
                value={formatMoney(paid)}
                unit={moneyUnit('short')}
                tone="good"
                data-testid="cashier-settle-paid"
              />
            )}
            {/* «مانده» depends on the recorded payments, which arrive a beat after
                the invoice: until they do the row holds its height with a block
                rather than a figure that is about to change. */}
            {paidLoaded ? (
              <MobileMoneyRow
                strong
                label={t('cashier.invoice.remaining')}
                value={formatMoney(outstanding)}
                unit={moneyUnit('short')}
                tone={outstanding > 0 ? 'danger' : 'good'}
                data-testid="cashier-settle-outstanding"
              />
            ) : (
              <MobileMoneyRowSkeleton strong />
            )}
            {/* Only reachable by discounting or repricing below what was already
                collected. There is no refund flow to settle it — surface the
                number so the cashier deals with it rather than reading a clean
                zero balance. */}
            {overpaid > 0 && (
              <MobileMoneyRow
                strong
                label={t('cashier.invoice.overpaid')}
                value={formatMoney(overpaid)}
                unit={moneyUnit('short')}
                tone="warn"
                data-testid="cashier-settle-overpaid"
              />
            )}
          </section>

          {/* ⑤ Issuance: the proforma becomes the numbered final invoice. Normally
              automatic when the visit is completed; offered here for the desk that
              settles before the doctor presses «پایان ویزیت». */}
          {!isVoid && !frozen && localInv.lineItems.length > 0 && issueInvoice && (
            <section data-testid="cashier-settle-issuance" style={{ display: 'grid', gap: '0.5rem' }}>
              <Alert variant="warning">{t('cashier.invoice.proformaExplainer')}</Alert>
              <Button
                onClick={handleIssue}
                disabled={issuing}
                style={{ width: '100%', minHeight: '2.75rem' }}
                data-testid="cashier-settle-issue"
              >
                {issuing ? t('common.state.saving') : t('cashier.invoice.issueFinal')}
              </Button>
            </section>
          )}

          {!isSettled && (
            <>
              {/* ⑥ مبلغ دریافتی */}
              <section data-testid="cashier-settle-amount" style={{ display: 'grid', gap: '0.375rem' }}>
                {/* «قیمت دستی» — the human fallback for a service the clinic
                    has not priced, shown for what it is and asked to say why
                    (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §D7). */}
                {isEmptyInvoice ? (
                  <div
                    data-testid="settle-manual-price"
                    style={{
                      padding: '0.5rem 0.75rem', borderRadius: '0.625rem',
                      background: C.amberLight, fontSize: '0.75rem',
                      lineHeight: 1.7, color: CT.warnInk,
                    }}
                  >
                    <div>{t('cashier.manualPrice.hint')}</div>
                    <TextInput
                      value={manualReason}
                      onChange={(e) => setManualReason(e.target.value)}
                      placeholder={t('cashier.manualPrice.reasonPlaceholder')}
                      aria-label={t('cashier.manualPrice.reason')}
                      data-testid="settle-manual-reason"
                      style={{ marginTop: '0.5rem' }}
                    />
                  </div>
                ) : null}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label
                    htmlFor="cashier-pay-amount"
                    style={{ flex: 1, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}
                  >
                    {isEmptyInvoice ? t('cashier.manualPrice.title') : t('cashier.invoice.amountReceived')}
                  </label>
                  {!isEmptyInvoice && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAmountTouched(false)}
                      style={{ minHeight: '2.75rem' }}
                      data-testid="cashier-settle-reset-amount"
                    >
                      {t('cashier.invoice.totalOutstandingWithAmount', { amount: formatMoney(outstanding) })}
                    </Button>
                  )}
                </div>
                {/* Toman — like the balance above it. Converted to rials once, in
                    handlePay. */}
                <MoneyInput currency={currency}
                  id="cashier-pay-amount"
                  value={payAmount}
                  onChange={(v) => { setAmountTouched(true); setPayAmount(v); }}
                  placeholder={isEmptyInvoice ? t('cashier.invoice.enterAmountToman') : undefined}
                  aria-label={isEmptyInvoice
                    ? t('cashier.invoice.visitFeeToman')
                    : t('cashier.invoice.amountReceivedToman')}
                  data-testid="cashier-settle-amount-input"
                  // The unit is the field's own chip now; the caption that used
                  // to say «تومان» under it went with the hand-drawn box.
                  inputStyle={{
                    minHeight: '3rem',
                    // ≥1rem or iOS Safari zooms the viewport on focus.
                    fontSize: '1.0625rem', fontWeight: 700,
                    ...((!isEmptyInvoice && payRial > outstanding + 0.5) ? { borderColor: C.red } : null),
                  }}
                />
              </section>

              {/* ⑦ روش پرداخت */}
              <section data-testid="cashier-settle-methods" style={{ display: 'grid', gap: '0.5rem' }}>
                <SectionHead title={t('cashier.invoice.paymentMethod')} />
                <div
                  role="radiogroup"
                  aria-label={t('cashier.invoice.paymentMethod')}
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' }}
                >
                  {Object.entries(methods).map(([key, m]) => {
                    const selected = method === key;
                    return (
                      <Button
                        key={key}
                        variant="outline"
                        role="radio"
                        aria-checked={selected}
                        disabled={!m.enabled}
                        onClick={() => m.enabled && setMethod(key)}
                        data-testid={`cashier-settle-method-${key}`}
                        data-state={selected ? 'selected' : 'idle'}
                        style={{
                          display: 'block', width: '100%', minHeight: '3.25rem',
                          height: 'auto', paddingBlock: '0.5rem', textAlign: 'start',
                          whiteSpace: 'normal',
                          border: `2px solid ${selected ? m.color : C.border}`,
                          background: !m.enabled ? C.bg : (selected ? m.bg : C.card),
                        }}
                      >
                        <span style={{
                          display: 'block', fontSize: '0.8125rem', fontWeight: 700,
                          color: selected ? m.color : C.textPrimary,
                        }}
                        >
                          {m.label}
                        </span>
                        {/* The reason a method cannot be used is visible text, not
                            a `title`: there is no hover on a phone. */}
                        <span style={{
                          display: 'block', fontSize: '0.6875rem', fontWeight: 400,
                          color: m.enabled ? C.textMuted : C.amber,
                        }}
                        >
                          {m.desc}
                        </span>
                      </Button>
                    );
                  })}
                </div>

                {/* The reference the server reconciles a POS / transfer /
                    third-party payment against — it refuses the payment without it. */}
                {activeMethod?.needsRef && (
                  <TextInput
                    label={activeMethod.refLabel}
                    value={reference}
                    onChange={(e) => setReference(e?.target ? e.target.value : e)}
                    placeholder={activeMethod.refLabel}
                    aria-label={activeMethod.refLabel}
                    dir="ltr"
                    data-testid="cashier-settle-reference"
                    inputStyle={{ minHeight: '2.75rem', fontSize: '1rem' }}
                  />
                )}

                {/* ⑧ «علی‌الحساب»: money the clinic already holds for this patient.
                    Settling out of it is a real payment (method CREDIT) that puts
                    nothing in today's drawer. */}
                {creditBalance > 0 && outstanding > 0 && !isEmptyInvoice && applyCredit && (
                  <div
                    data-testid="cashier-settle-credit"
                    style={{
                      display: 'grid', gap: '0.5rem',
                      padding: '0.625rem 0.75rem', borderRadius: '0.625rem',
                      background: C.tealLight, border: `1px solid ${C.teal}`,
                    }}
                  >
                    <MobileMoneyRow
                      label={t('cashier.credit.balanceLabel')}
                      value={formatMoney(creditBalance)}
                      unit={moneyUnit('short')}
                    />
                    <Button
                      onClick={handleApplyCredit}
                      disabled={saving}
                      style={{ width: '100%', minHeight: '2.75rem' }}
                      data-testid="cashier-settle-apply-credit"
                    >
                      {t('cashier.credit.applyAction', {
                        amount: formatMoney(Math.min(creditBalance, outstanding)),
                      })}
                    </Button>
                  </div>
                )}
              </section>
            </>
          )}

          {isSettled && (
            <div style={{ textAlign: 'center', padding: '1rem 0' }} data-testid="cashier-settle-receipt">
              <div style={{ fontSize: '2rem' }} aria-hidden="true">{overpaid > 0 ? '⚠️' : '✅'}</div>
              <p style={{
                margin: '0.5rem 0 0', fontSize: '0.9375rem', fontWeight: 700,
                color: overpaid > 0 ? C.amber : C.green,
              }}
              >
                {overpaid > 0 ? t('cashier.invoice.settledOverpaid') : t('cashier.invoice.fullySettled')}
              </p>
              {overpaid > 0 && (
                <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.8 }}>
                  {t('cashier.invoice.refundDue', { amount: formatMoney(overpaid) })}
                </p>
              )}
            </div>
          )}
        </div>
      </MobileSheet>

      {/* Reopening a settled invoice is a real accounting event, not a UI toggle:
          the patient owes again and the removal rule flips. Make the cashier say
          yes, with the whole consequence — including that the added line will no
          longer be removable. */}
      <ConfirmDialog
        open={reopenAsk}
        onOpenChange={(v) => { if (!v) setReopenAsk(false); }}
        title={t('cashier.reopen.title')}
        description={t('cashier.reopen.explainer')}
        confirmLabel={t('cashier.reopen.confirm')}
        cancelLabel={t('common.actions.cancel')}
        onConfirm={() => { setReopenAsk(false); setAddingItem(true); }}
      />

      {/* «تغییر خدمت» — a second sheet over the task sheet. Allowed: it is a
          picker for the sheet beneath it, and it asks before it closes dirty
          (the overlay-history law). */}
      <MobileSheet
        open={serviceAsk}
        onClose={() => setServiceAsk(false)}
        title={t('cashier.changeService.title')}
        closeLabel={t('common.actions.close')}
        data-testid="settle-change-service-sheet"
      >
        <div style={{ padding: '0.875rem 1rem' }}>
          <ChangeServiceDialog
            open={serviceAsk}
            current={settlement?.service?.declarationId || null}
            options={settlement?.serviceOptions || []}
            hasMoney={paid > 0}
            onCancel={() => setServiceAsk(false)}
            onConfirm={async (declarationId, reason) => {
              const next = await onChangeService(declarationId, reason);
              setServiceAsk(false);
              if (next?.invoice) setLocalInv(next.invoice);
            }}
          />
        </div>
      </MobileSheet>

      <AddItemSheet
        open={addingItem}
        onClose={() => setAddingItem(false)}
        tariffs={tariffs}
        orders={isVoid ? [] : patientOrders}
        onPickTariff={handlePickTariff}
        onBillOrder={handleBillOrder}
      />

      {editingItem && (
        <LineEditSheet
          key={editingItem.id}
          open
          item={editingItem}
          canOverride={canOverrideInsurance}
          onClose={() => setEditingId(null)}
          onSave={async (patch) => { await handleEditItem(editingItem.id, patch); setEditingId(null); }}
          onOverride={handleOverrideInsurance}
        />
      )}

      {/* Mounted only while open: it seeds its fields from the invoice at mount,
          and a sheet kept alive across a discount change would reopen showing the
          figure the operator just replaced. */}
      {discountOpen && (
        <InvoiceDiscountSheet
          open
          invoice={localInv}
          subtotal={subtotal}
          onClose={() => setDiscountOpen(false)}
          onSave={handleSetDiscount}
        />
      )}
    </>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

function SectionHead({ title }) {
  return (
    <h3 style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
      {title}
    </h3>
  );
}

/**
 * One billed line. Its two operations live in a row menu rather than as hover
 * icons — the desktop's ✎/🗑 are pointer affordances with `title`-only
 * explanations, neither of which exists on a phone.
 *
 * Edit stays available after a payment and on a settled invoice, exactly as on
 * the desk: a line billed at the wrong price has to remain correctable. Only a
 * voided or ISSUED invoice freezes it.
 */
// The roles that have a name worth showing. PRODUCT and LEGACY are absent for
// the same reason as on the desk: one already reads as what it is, the other
// has no role to announce.
const ROLE_LABELS = new Set(['PRIMARY', 'PLANNED', 'PERFORMED', 'DESK', 'CREDIT_NOTE']);

function LineCard({
  item, canEdit, canRemove, onEdit, onRemove, roleLabel = '', onChangeService = null,
}) {
  const { t, moneyUnit, formatNumber, formatMoney } = useTranslation();
  const meta = ITEM_TYPE_META(t)[item.itemType] || ITEM_TYPE_META(t).VISIT;
  const isSettledLine = item.settlementStatus === 'SETTLED';

  const actions = [
    canEdit && { id: 'edit', label: t('cashier.line.edit'), icon: <Pencil size={16} />, onClick: onEdit },
    canRemove && { id: 'remove', label: t('cashier.line.delete'), icon: <Trash2 size={16} />, tone: 'danger', onClick: onRemove },
    // «تغییر خدمت» is a ROW in the menu rather than a chip on the card: on a
    // phone the card is already three lines of money, and a second control
    // beside the name is the one a thumb hits by accident.
    onChangeService && {
      id: 'service', label: t('cashier.changeService.action'),
      icon: <Pencil size={16} />, onClick: onChangeService,
    },
  ].filter(Boolean);

  return (
    <div
      data-testid={`cashier-settle-line-${item.id}`}
      data-state={isSettledLine ? 'settled' : 'open'}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
        padding: '0.625rem 0.75rem',
        border: `1px solid ${C.border}`, borderRadius: '0.625rem',
        background: C.card, opacity: isSettledLine ? 0.7 : 1,
      }}
    >
      <span style={{ fontSize: '1.125rem', flexShrink: 0 }} aria-hidden="true">{meta.icon}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          dir="auto"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap',
            fontSize: '0.8125rem', fontWeight: 600, color: C.textPrimary,
          }}
        >
          {item.description}
          {roleLabel ? (
            <span style={{
              fontSize: '0.625rem', fontWeight: 700, padding: '0.0625rem 0.375rem',
              borderRadius: '0.25rem', background: C.tealLight, color: C.teal,
            }}>
              {roleLabel}
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.125rem' }}>
          {meta.label}
          {item.quantity > 1 ? ` · ${formatNumber(item.quantity)}×` : ''}
          {' '}
          {formatMoney(item.unitTariff, { currency: item?.currency })} {moneyUnit('short', item?.currency)}
        </div>
        {item.discount > 0 && (
          <div style={{ fontSize: '0.6875rem', color: C.green }}>
            {t('cashier.line.discountSuffix')}{' '}
            {discountLabel(item, t, formatNumber, formatMoney)}
          </div>
        )}
        {item.insuranceShare > 0 && (
          <div style={{ fontSize: '0.6875rem', color: C.amber }}>
            {t('cashier.line.insuranceSuffix')}{' '}
            {formatMoney(item.insuranceShare, { currency: item?.currency })} {moneyUnit('short', item?.currency)}
          </div>
        )}
        <div
          {...LTR_TEXT}
          style={{ fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary, marginTop: '0.25rem' }}
        >
          {formatMoney(item.patientPayable, { currency: item?.currency })}
          <span style={{ fontSize: '0.625rem', fontWeight: 400, color: C.textMuted, marginInlineStart: '0.25rem' }}>
            {moneyUnit('short', item?.currency)}
          </span>
        </div>
      </div>

      <MobileRowMenu
        items={actions}
        subject={item.description}
        title={t('cashier.mobile.lineActions')}
        data-testid={`cashier-settle-line-menu-${item.id}`}
      />
    </div>
  );
}

/**
 * Fixing a line that was billed wrong.
 *
 * The form holds TOMAN as raw strings; only rials are ever sent, converted once
 * at the boundary. `insuranceShare` is offered ONLY when nothing computed it: on
 * a line the service priced from the patient's cover, a free-typed share is
 * exactly the defect the insurance feature exists to close — that line gets the
 * read-only explanation and, for `payments.manage` only, a deliberate override
 * with a mandatory reason.
 */
function LineEditSheet({ item, open, onClose, onSave, onOverride, canOverride }) {
  const { t, moneyUnit, formatMoney, currency, toDisplayAmount } = useTranslation();
  const insState = lineInsuranceState(item);
  const computedShare = insState !== SOURCE.NONE;
  const pctOnOpen = (item.discountType || 'AMOUNT') === 'PERCENT';

  const [form, setForm] = React.useState({
    description: item.description || '',
    quantity: String(item.quantity ?? 1),
    unitTariff: String(toDisplayAmount(item.unitTariff ?? 0) ?? 0),
    discountType: item.discountType || 'AMOUNT',
    // A PERCENT discountValue is a percent, not money — it does not convert.
    discountValue: String((pctOnOpen ? item.discountValue : toDisplayAmount(item.discountValue ?? 0)) ?? 0),
    insuranceShare: String(toDisplayAmount(item.insuranceShare ?? 0) ?? 0),
  });
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');

  const set = (key, value) => { setDirty(true); setForm((f) => ({ ...f, [key]: value })); };

  const { isPct, qty, dval, unit, ins, disc, payable } = lineEditPreview(form, item.currency || currency);
  const invalid = validateLineEdit(t, form, item.currency || currency);

  const submit = async () => {
    if (invalid) { setErr(invalid); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        description: form.description.trim(),
        quantity: qty,
        // Rials from here down — the toman above never leaves this sheet.
        unitTariff: unit,
        discountType: form.discountType,
        discountValue: isPct ? dval : disc,
        // A computed share is not the cashier's to resend: the service re-derives
        // it from the snapshot on the line, and echoing the form's copy back would
        // silently turn it into a manual figure.
        insuranceShare: computedShare ? item.insuranceShare : ins,
      });
    } catch (e) {
      setErr(moneyErrorText(e, t) || t('errors.cashier.lineEditFailed'));
      setSaving(false);
    }
  };

  return (
    <MobileFormSheet
      open={open}
      onClose={onClose}
      title={t('cashier.line.editTitle')}
      dirty={dirty}
      submitting={saving}
      submitDisabled={Boolean(invalid)}
      submitLabel={t('common.actions.save')}
      onSubmit={submit}
      error={err || invalid || null}
      data-testid="cashier-line-edit-sheet"
    >
      <MobileField label={t('accounting.fields.lineDescription')} htmlFor="cashier-line-description">
        <TextInput
          id="cashier-line-description"
          value={form.description}
          onChange={(e) => set('description', e?.target ? e.target.value : e)}
          placeholder={t('accounting.fields.lineDescription')}
          aria-label={t('accounting.fields.lineDescription')}
          data-testid="cashier-line-description"
          inputStyle={{ minHeight: '2.75rem', fontSize: '1rem' }}
        />
      </MobileField>

      <MobileField label={t('accounting.fields.quantity')} htmlFor="cashier-line-quantity">
        <TextInput
          id="cashier-line-quantity"
          type="number"
          inputMode="numeric"
          value={form.quantity}
          onChange={(e) => set('quantity', e?.target ? e.target.value : e)}
          aria-label={t('accounting.fields.quantity')}
          dir="ltr"
          data-testid="cashier-line-quantity"
          inputStyle={{ minHeight: '2.75rem', fontSize: '1rem' }}
        />
      </MobileField>

      <MobileField label={t('accounting.fields.unitPriceToman')} htmlFor="cashier-line-unit">
        <MoneyInput currency={currency}
          id="cashier-line-unit"
          value={form.unitTariff}
          onChange={(v) => set('unitTariff', v)}
          aria-label={t('accounting.fields.unitPriceToman')}
          data-testid="cashier-line-unit"
          inputStyle={FIELD_STYLE}
        />
      </MobileField>

      <MobileField
        label={t('accounting.fields.discount')}
        hint={isPct && disc > 0
          ? `${t('accounting.fields.equivalent')} ${formatMoney(disc)} ${moneyUnit()}`
          : undefined}
      >
        <MoneyInput currency={currency}
          allowDecimal
          value={form.discountValue}
          onChange={(v) => set('discountValue', v)}
          aria-label={t('accounting.fields.discount')}
          data-testid="cashier-line-discount"
          unitLabel={isPct ? t('cashier.discount.percentSymbol') : moneyUnit('field')}
          inputStyle={FIELD_STYLE}
        />
        <div
          role="radiogroup"
          aria-label={t('accounting.fields.discount')}
          style={{ display: 'flex', gap: '0.375rem', marginTop: '0.375rem' }}
        >
          {[
            { key: 'AMOUNT', label: moneyUnit(), name: t('cashier.discount.byAmount') },
            { key: 'PERCENT', label: t('cashier.discount.percentSymbol'), name: t('cashier.discount.byPercent') },
          ].map((type) => (
            <Button
              key={type.key}
              variant={form.discountType === type.key ? 'subtle' : 'outline'}
              role="radio"
              aria-checked={form.discountType === type.key}
              aria-label={type.name}
              onClick={() => set('discountType', type.key)}
              style={{ flex: 1, minHeight: '2.75rem' }}
              data-testid={`cashier-line-discount-${type.key}`}
            >
              {type.label}
            </Button>
          ))}
        </div>
      </MobileField>

      {computedShare ? (
        <InsurancePanel item={item} canOverride={canOverride} onOverride={onOverride} />
      ) : (
        <MobileField label={t('accounting.fields.insuranceShareToman')} htmlFor="cashier-line-insurance">
          <MoneyInput currency={currency}
            id="cashier-line-insurance"
            value={form.insuranceShare}
            onChange={(v) => set('insuranceShare', v)}
            aria-label={t('accounting.fields.insuranceShareToman')}
            data-testid="cashier-line-insurance"
            inputStyle={FIELD_STYLE}
          />
        </MobileField>
      )}

      {/* What the service will compute — same formula, so there is no surprise
          after saving. */}
      <MobileMoneyRow
        strong
        label={t('cashier.line.payableAfterEdit')}
        value={Number.isFinite(payable) ? formatMoney(payable) : '—'}
        unit={moneyUnit('short')}
        data-testid="cashier-line-payable"
      />
    </MobileFormSheet>
  );
}

/**
 * The insurer's share, explained rather than offered as a blank.
 *
 * Read-only by default: the explanation says WHICH insurer and under what
 * فرانشیز, so the number answers for itself. «ویرایش» appears only for
 * `payments.manage`, which is what stops a منشی putting the share back to a typed
 * guess, and the override records what the rules said beside what the person
 * decided.
 */
function InsurancePanel({ item, canOverride, onOverride }) {
  const { t, moneyUnit, formatMoney, currency, toDisplayAmount, fromDisplayAmount } = useTranslation();
  const nameOf = useInsurerName();
  const [editing, setEditing] = React.useState(false);
  const [amount, setAmount] = React.useState(String(toDisplayAmount(item.insuranceShare ?? 0) ?? 0));
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const state = lineInsuranceState(item);
  const explanation = explainLegs(item, { insurerName: null, t, nameOf });
  const gross = (item.totalAmount || 0) - (item.discount || 0);

  const commit = async () => {
    const refusal = validateInsuranceOverride(t, { amountToman: amount, reason, gross, currency: item.currency || currency });
    if (refusal) { setErr(refusal); return; }
    setBusy(true); setErr('');
    try {
      await onOverride(item.id, fromDisplayAmount(amount), reason.trim());
      setEditing(false);
      setReason('');
    } catch (e) {
      setErr(moneyErrorText(e, t) || t('errors.cashier.lineEditFailed'));
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="cashier-line-insurance"
      style={{
        display: 'grid', gap: '0.375rem',
        padding: '0.625rem 0.75rem',
        border: `1px solid ${C.border}`, borderRadius: '0.625rem', background: C.card,
      }}
    >
      <MobileMoneyRow
        label={t('insurance.share.insurer')}
        value={formatMoney(item.insuranceShare || 0, { currency: item?.currency })}
        unit={moneyUnit('short', item?.currency)}
        tone="warn"
        // Joined on the phone, where a row's hint is one line by design — the
        // desktop drawer has the room to give each insurer its own.
        hint={explanation.length ? explanation.join(' · ') : undefined}
        data-testid="cashier-line-insurance-amount"
      />

      {state === SOURCE.MANUAL && (
        <Alert variant="neutral">{t('insurance.override.applied')}</Alert>
      )}
      {state === SOURCE.UNAVAILABLE && (
        <Alert variant="warning">{t('insurance.state.unavailable')}</Alert>
      )}

      {canOverride && !editing && (
        <Button
          variant="outline"
          onClick={() => setEditing(true)}
          style={{ width: '100%', minHeight: '2.75rem' }}
          data-testid="cashier-line-insurance-edit"
        >
          {t('insurance.actions.editShare')}
        </Button>
      )}

      {editing && (
        <>
          {item.computedInsuranceShare != null && (
            <p style={{ margin: 0, fontSize: '0.6875rem', color: C.textMuted }}>
              {t('insurance.override.computedWas', {
                amount: `${formatMoney(item.computedInsuranceShare, { currency: item?.currency })} ${moneyUnit('short', item?.currency)}`,
              })}
            </p>
          )}
          <MoneyInput currency={currency}
            value={amount}
            onChange={setAmount}
            aria-label={t('insurance.share.insurer')}
            data-testid="cashier-line-insurance-amount-input"
            inputStyle={FIELD_STYLE}
          />
          <TextInput
            value={reason}
            onChange={(e) => setReason(e?.target ? e.target.value : e)}
            placeholder={t('insurance.override.reasonPlaceholder')}
            aria-label={t('insurance.override.reason')}
            data-testid="cashier-line-insurance-reason"
            inputStyle={{ minHeight: '2.75rem', fontSize: '1rem' }}
          />
          {err ? <Alert variant="error" role="alert">{err}</Alert> : null}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              onClick={commit}
              disabled={busy}
              style={{ flex: 1, minHeight: '2.75rem' }}
              data-testid="cashier-line-insurance-save"
            >
              {busy ? t('common.state.saving') : t('common.actions.save')}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setEditing(false); setErr(''); }}
              style={{ minHeight: '2.75rem' }}
            >
              {t('common.actions.cancel')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The invoice-wide discount.
 *
 * Its base is the PATIENT'S SHARE, not the gross, and unlike a line discount it
 * is clamped to that base — a fixed discount can outlive the lines it was sized
 * against, and the service clamps it the same way. The preview says what will
 * actually be applied.
 */
function InvoiceDiscountSheet({ open, invoice, subtotal, onClose, onSave }) {
  const { t, moneyUnit, formatNumber, formatMoney, currency, toDisplayAmount, fromDisplayAmount } = useTranslation();
  const pctOnOpen = (invoice.discountType || 'AMOUNT') === 'PERCENT';
  const [type, setType] = React.useState(invoice.discountType || 'AMOUNT');
  const [value, setValue] = React.useState(
    String((pctOnOpen ? invoice.discountValue : toDisplayAmount(invoice.discountValue ?? 0)) ?? 0),
  );
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');

  const isPct = type === 'PERCENT';
  const v = value === '' ? NaN : Number(value);
  // What the service will store: a percent as-is, an amount in rials.
  const saveValue = isPct ? v : fromDisplayAmount(value);
  const resolved = Math.min(resolveDiscount(type, saveValue, subtotal), subtotal);
  const invalid = validateInvoiceDiscount(t, { type, value });

  const run = async (nextType, nextValue, failKey) => {
    setSaving(true); setErr('');
    try {
      await onSave(nextType, nextValue);
    } catch (e) {
      setErr(moneyErrorText(e, t) || t(failKey));
      setSaving(false);
    }
  };

  return (
    <MobileFormSheet
      open={open}
      onClose={onClose}
      title={t('cashier.discount.wholeInvoice')}
      dirty={dirty}
      submitting={saving}
      submitDisabled={Boolean(invalid)}
      submitLabel={t('cashier.discount.apply')}
      onSubmit={() => (invalid ? setErr(invalid) : run(type, saveValue, 'errors.cashier.discountSaveFailed'))}
      error={err || invalid || null}
      snap={0.7}
      fullHeight={false}
      data-testid="cashier-invoice-discount-sheet"
      secondary={invoice.discountValue > 0 ? (
        <Button
          variant="outline"
          onClick={() => run('AMOUNT', 0, 'errors.cashier.discountRemoveFailed')}
          disabled={saving}
          style={{ minHeight: '2.75rem' }}
          data-testid="cashier-invoice-discount-remove"
        >
          {t('cashier.discount.remove')}
        </Button>
      ) : null}
    >
      <MobileField
        label={t('cashier.discount.wholeInvoice')}
        hint={isPct
          ? t('cashier.discount.percentExplainer', {
            percent: formatNumber(Number.isFinite(v) ? v : 0),
            base: formatMoney(subtotal),
            amount: formatMoney(resolved),
          })
          : t('cashier.discount.amountExplainer', { base: formatMoney(subtotal) })}
      >
        <MoneyInput currency={currency}
          allowDecimal
          value={value}
          onChange={(next) => { setDirty(true); setValue(next); }}
          aria-label={t('cashier.discount.wholeInvoice')}
          data-testid="cashier-invoice-discount-value"
          unitLabel={isPct ? t('cashier.discount.percentSymbol') : moneyUnit('field')}
          inputStyle={FIELD_STYLE}
        />
        <div
          role="radiogroup"
          aria-label={t('accounting.fields.discount')}
          style={{ display: 'flex', gap: '0.375rem', marginTop: '0.375rem' }}
        >
          {[
            { key: 'AMOUNT', label: moneyUnit(), name: t('cashier.discount.byAmount') },
            { key: 'PERCENT', label: t('cashier.discount.percentSymbol'), name: t('cashier.discount.byPercent') },
          ].map((option) => (
            <Button
              key={option.key}
              variant={type === option.key ? 'subtle' : 'outline'}
              role="radio"
              aria-checked={type === option.key}
              aria-label={option.name}
              onClick={() => { setDirty(true); setType(option.key); }}
              style={{ flex: 1, minHeight: '2.75rem' }}
              data-testid={`cashier-invoice-discount-${option.key}`}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </MobileField>
    </MobileFormSheet>
  );
}

/**
 * Adding a line: this patient's own un-billed in-house orders first, then the
 * clinic's declared services.
 *
 * An un-billed order is work the doctor ordered and the clinic performs itself,
 * with no invoice line yet — one tap bills it, and the line traces back to the
 * order that caused it. Billed ones drop out: their line already exists.
 */
function AddItemSheet({ open, onClose, tariffs, orders, onPickTariff, onBillOrder }) {
  const { t, moneyUnit, formatNumber, formatMoney } = useTranslation();
  const unbilled = (orders || []).filter((o) => o.billed === false);
  const active = (tariffs || []).filter((tariff) => tariff.isActive);

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={t('cashier.line.addParaclinical')}
      closeLabel={t('common.actions.close')}
      snap={0.85}
      data-testid="cashier-add-item-sheet"
    >
      <div style={{ padding: '0.875rem 1rem', display: 'grid', gap: '1rem' }}>
        {unbilled.length > 0 && (
          <section style={{ display: 'grid', gap: '0.5rem' }} data-testid="cashier-add-item-orders">
            <SectionHead title={t('cashier.line.patientOrders')} />
            {unbilled.map((o) => (
              <PickRow
                key={o.id}
                testId={`cashier-add-item-order-${o.id}`}
                title={o.description}
                badge={o.sessionCount > 1
                  ? `×${formatNumber(o.sessionCount)} ${t('common.units.session')}`
                  : null}
                price={`${formatMoney(o.unitPrice, { currency: o?.currency })} ${moneyUnit('short', o?.currency)}`}
                onClick={() => onBillOrder(o)}
              />
            ))}
          </section>
        )}

        <section style={{ display: 'grid', gap: '0.5rem' }} data-testid="cashier-add-item-tariffs">
          <SectionHead title={t('cashier.line.addParaclinical')} />
          {active.length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.75rem', color: C.textMuted }}>
              {t('accounting.empty.noLineItems')}
            </p>
          ) : active.map((tariff) => (
            <PickRow
              key={tariff.id}
              testId={`cashier-add-item-tariff-${tariff.id}`}
              title={tariff.description}
              price={`${formatMoney(tariff.unitPrice, { currency: tariff?.currency })} ${moneyUnit('short', tariff?.currency)}`}
              onClick={() => onPickTariff(tariff)}
            />
          ))}
        </section>
      </div>
    </MobileSheet>
  );
}

function PickRow({ title, badge, price, onClick, testId }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      data-testid={testId}
      style={{
        display: 'block', width: '100%', minHeight: '2.75rem',
        height: 'auto', paddingBlock: '0.5rem', textAlign: 'start', whiteSpace: 'normal',
      }}
    >
      <span dir="auto" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: C.textPrimary }}>
        {title}
        {badge ? (
          <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.navy, marginInlineStart: '0.375rem' }}>
            {badge}
          </span>
        ) : null}
      </span>
      <span {...LTR_TEXT} style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 400, color: C.textMuted }}>
        {price}
      </span>
    </Button>
  );
}

// Reaches the kit's real `<input>` via `inputStyle` (v0.13.0). The box —
// border, radius, height, the «تومان» chip — is the kit's own now, so only the
// two phone rules remain: ≥1rem or iOS Safari zooms the viewport on focus, and
// a bold figure for money.
const FIELD_STYLE = { minHeight: '2.75rem', fontSize: '1rem', fontWeight: 700 };

// The invoice is read in ITS currency (docs/clinic-currency/CONTRACTS.md §0.5).
export default inDocumentCurrency(MobileSettleSheet, (p) => p.invoice);
